import { apiErrorMessage, isApiError } from '@agos/api-client'
import type { OrderListItem } from '@agos/api-client/model'
import {
  useGetCategories,
  useGetGiftsProductRules,
  useGetGiftsRules,
  useGetKitchenRecipes,
  useGetProducts,
} from '@agos/api-client/menu'
import {
  getGetOrdersQueryKey,
  postOrdersOrderIdConfirmStatus,
  useGetOrders,
  useGetOrdersAvgCompletionTime,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import { useGetSettingsGeneral } from '@agos/api-client/platform'
import { ALL_CHANNELS, chimeToggleLabel, filterByChannel, filterChannelsOf, offeredChannels, sanitiseSelection, selectAll, selectionNote, startOfDay, T, toggleChannel, tt, useArrivalChime, useAuth, type ChannelSelection } from '@agos/shared'
import { ChannelTabs, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, ChefHat, RefreshCw, ShoppingBag, Store, Timer, Truck, Volume2, VolumeX } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { CountdownRing } from './CountdownRing'
import { KitchenOrderCard } from './KitchenOrderCard'
import {
  compareKitchenTickets,
  isPreorderDueNow,
  nextItemProgress,
  type ItemProgress,
} from './elapsed'
import { kitchenLinesOf, type KitchenLine, type KitchenVisibilityMaps } from './kitchen-items'
import type { KitchenDescriptions } from './KitchenItemCard'
import { buildRecipeIndex, type RecipeIngredient, type RecipeRow } from './recipes'

/**
 * `/kitchen` — the kitchen display, ported from legacy `pages/kitchen.tsx`.
 *
 * §14.1: polling *is* the realtime layer, and ten seconds is the interval a
 * kitchen has been run on. It is copied exactly. The visible countdown ring is
 * not decoration either — a cook who cannot see when the board last refreshed
 * has no way to tell a quiet kitchen from a broken screen.
 *
 * The screen paints its own dark background rather than the app's, because it
 * hangs on a wall and is read from a metre away (§14.9).
 */

const REFRESH_SECONDS = 10
const KITCHEN_STATUSES = ['new', 'preparing']
const DEFAULT_PREORDER_THRESHOLD_MINUTES = 60
/** How long a handed-over ticket stays on screen before it disappears. */
const HANDOVER_LINGER_MS = 1500
const ORDERS_PAGE_SIZE = '500'

export function KitchenPage() {
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()

  const [secondsToRefresh, setSecondsToRefresh] = useState(REFRESH_SECONDS)
  const [now, setNow] = useState(() => Date.now())
  const [completing, setCompleting] = useState<ReadonlySet<number>>(new Set())
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set())
  const [itemProgress, setItemProgress] = useState<Record<string, ItemProgress>>({})
  /**
   * §9.5's channel filter. Not persisted across sessions on purpose: a kitchen screen left filtered
   * overnight is how an order gets missed, so the next shift opens on everything.
   */
  const [channelSelection, setChannelSelection] = useState<ChannelSelection>(ALL_CHANNELS)
  const previousNewCount = useRef<number | null>(null)
  const chime = useArrivalChime()

  const today = startOfDay(new Date()).toISOString()
  const { data: ordersResult, isLoading } = useGetOrders(
    { date_from: today, page_size: ORDERS_PAGE_SIZE },
    { query: { refetchInterval: REFRESH_SECONDS * 1000 } },
  )
  const orders = useMemo(() => ordersResult?.data ?? [], [ordersResult])

  const patchOrder = usePatchOrdersOrderId()
  const { data: products } = useGetProducts({})
  // §10A.15 — the recipes a cook may read. Guarded {main_admin, admin, cook, waiter}, unlike the
  // admin-only editing routes beside it, and carrying no costs.
  const { data: kitchenRecipes } = useGetKitchenRecipes()
  const {
    data: categories,
    isSuccess: categoriesLoaded,
    isError: categoriesFailed,
  } = useGetCategories()
  const { data: amountGiftRules } = useGetGiftsRules()
  const { data: productGiftRules } = useGetGiftsProductRules()
  const { data: salesChannels } = useGetSalesChannels()
  const { data: settings } = useGetSettingsGeneral()
  const { data: avgCompletion } = useGetOrdersAvgCompletionTime()

  const preorderThresholdMinutes = readThreshold(settings)

  /**
   * The countdown drives both the visible ring and `now`, so every elapsed time
   * on the board advances together and none of them is fresher than the data
   * (FB-039). It does not fetch: the query owns its own interval, and a second
   * invalidation on the same schedule would double the request rate.
   */
  useEffect(() => {
    const tick = setInterval(() => {
      setNow(Date.now())
      setSecondsToRefresh((remaining) => (remaining <= 1 ? REFRESH_SECONDS : remaining - 1))
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
    setSecondsToRefresh(REFRESH_SECONDS)
  }

  const categoriesReady = categoriesLoaded || categoriesFailed

  const visibility: KitchenVisibilityMaps = useMemo(
    () => buildVisibilityMaps(products, categories, amountGiftRules, productGiftRules),
    [products, categories, amountGiftRules, productGiftRules],
  )

  const descriptions: KitchenDescriptions = useMemo(
    () => buildDescriptions(products, kitchenRecipes),
    [products, kitchenRecipes],
  )

  const linesByOrder = useMemo(() => {
    const byOrder: Record<number, KitchenLine[]> = {}
    for (const order of orders) {
      byOrder[order.id ?? 0] = kitchenLinesOf(order.items, visibility, categoriesReady)
    }
    return byOrder
  }, [orders, visibility, categoriesReady])

  const activeOrders = useMemo(
    () =>
      orders
        .filter((order) => {
          const id = order.id ?? 0
          if (completing.has(id)) return true
          if (!KITCHEN_STATUSES.includes(order.status ?? '')) return false
          // A ticket whose every line was filtered out is a ticket with nothing
          // for the kitchen to make — drinks only, say.
          if ((linesByOrder[id]?.length ?? 0) === 0) return false
          return isPreorderDueNow(order.scheduledAt, preorderThresholdMinutes, now)
        })
        .sort(compareKitchenTickets),
    [orders, completing, linesByOrder, preorderThresholdMinutes, now],
  )

  /**
   * Pre-orders still outside the threshold.
   *
   * **§9.318, the owner's Q8** - *"Predobjednávky - not showing in kuchyna, rozvoz - nástenka etc."*
   * This was counted in the header and rendered nowhere, on the reasoning that the orders *"belong on
   * the dispatch board until they come due"* - and that board dropped anything scheduled for a later
   * day, saying it lived on the pre-order screen. Each handed it to the other.
   *
   * They are drawn now, in a section of their own below the active cards, sorted by when they are due
   * and marked so nobody starts one early. **That marking is the point**: the reason they were hidden
   * is real, and a cook who cooks tomorrow's order tonight has wasted the food.
   */
  const queuedPreorders = useMemo(
    () =>
      orders.filter((order) => {
        if (!['new', 'preparing', 'pending'].includes(order.status ?? '')) return false
        if (!order.scheduledAt) return false
        if (isPreorderDueNow(order.scheduledAt, preorderThresholdMinutes, now)) return false
        if (order.status === 'pending') return true
        return (linesByOrder[order.id ?? 0]?.length ?? 0) > 0
      })
        // Soonest first: the section is read to know what is coming, not in arrival order.
        .sort(
          (left, right) =>
            new Date(left.scheduledAt ?? 0).getTime() - new Date(right.scheduledAt ?? 0).getTime(),
        ),
    [orders, linesByOrder, preorderThresholdMinutes, now],
  )

  // The channels the server knows, plus any switched-off one whose orders are still on the board.
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])
  const usedSlugs = useMemo(() => activeOrders.map((order) => order.type ?? ''), [activeOrders])
  const offered = useMemo(() => offeredChannels(channels, usedSlugs), [channels, usedSlugs])
  const selection = sanitiseSelection(channelSelection, offered)
  const shownOrders = useMemo(
    () => filterByChannel(activeOrders, selection),
    [activeOrders, selection],
  )

  const newOrders = shownOrders.filter((order) => order.status === 'new')
  const preparingOrders = shownOrders.filter(
    (order) => order.status === 'preparing' && !completing.has(order.id ?? 0),
  )
  // Counted over the whole board, not the filtered one: a tab has to say what tapping it would bring.
  const channelCounts = useMemo(() => {
    const tally: Record<string, number> = {}
    for (const order of activeOrders) {
      const slug = order.type ?? ''
      if (slug !== '') tally[slug] = (tally[slug] ?? 0) + 1
    }
    return tally
  }, [activeOrders])
  const counts = {
    restaurant: channelCounts.restaurant ?? 0,
    delivery: channelCounts.delivery ?? 0,
    bar: channelCounts.bar ?? 0,
  }

  /**
   * Announce arrivals, but not the first render — a cook opening the screen to
   * six waiting orders does not need to be told six orders arrived.
   */
  useEffect(() => {
    const previous = previousNewCount.current
    if (previous !== null && newOrders.length > previous) {
      const arrived = newOrders.length - previous
      toast({
        title: tt('🔔 Nová objednávka!'),
        description: `${tt('Pribudla')} ${arrived} ${tt('nová objednávka v kuchyni.')}`,
      })
      // §9.110 — **gentle here, and deliberately not the urgent pattern.** Dispatch keeps `urgent`
      // for an order nobody has accepted, which is rare. In a kitchen an arrival is the normal event
      // of a service; three double-beeps for every one of them is noise, and noise gets switched off
      // — which would cost the owner the reason they asked for sound in here at all.
      chime.play('gentle')
    }
    previousNewCount.current = newOrders.length
  }, [newOrders.length, chime])

  const toggleItem = (key: string) => {
    setItemProgress((current) => ({
      ...current,
      [key]: nextItemProgress(current[key] ?? 'idle'),
    }))
  }

  const markBusy = (orderId: number, isBusy: boolean) =>
    setBusy((current) => {
      const next = new Set(current)
      if (isBusy) next.add(orderId)
      else next.delete(orderId)
      return next
    })

  const advance = async (order: OrderListItem) => {
    const orderId = order.id ?? 0
    if (busy.has(orderId)) return

    if (order.status === 'new') {
      markBusy(orderId, true)
      patchOrder.mutate(
        { orderId, data: { status: 'preparing' } },
        {
          onSuccess: () => {
            refresh()
            toast({
              title: `${tt('Objednávka #')}${order.orderNumber}`,
              description: tt('Presunutá do prípravy.'),
            })
          },
          onSettled: () => markBusy(orderId, false),
        },
      )
      return
    }

    // Handing food to the pass goes through confirm-status with
    // `expectedStatus`, so two cooks tapping the same ticket cannot both move
    // it — the second gets a 409 telling them what it became.
    markBusy(orderId, true)
    setCompleting((current) => new Set(current).add(orderId))
    try {
      await postOrdersOrderIdConfirmStatus(orderId, {
        newStatus: 'quality_check',
        reason: tt('Hotová z kuchyne'),
        expectedStatus: order.status,
      })
      refresh()
      toast({
        title: `${tt('Objednávka #')}${order.orderNumber}`,
        description: tt('Hotová - ide na kontrolu.'),
      })
      setTimeout(() => {
        setCompleting((current) => {
          const next = new Set(current)
          next.delete(orderId)
          return next
        })
      }, HANDOVER_LINGER_MS)
    } catch (caught) {
      setCompleting((current) => {
        const next = new Set(current)
        next.delete(orderId)
        return next
      })
      const currentStatus =
        isApiError(caught) && caught.status === 409
          ? readCurrentStatus(caught.data)
          : undefined
      if (currentStatus) {
        refresh()
        toast({
          variant: 'destructive',
          title: `${tt('Objednávka #')}${order.orderNumber}`,
          description: `${tt('Stav sa zmenil – aktuálny stav:')} „${currentStatus}“. ${tt('Stránka bola obnovená.')}`,
        })
      } else {
        toast({
          variant: 'destructive',
          title: tt('Chyba'),
          description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav.')),
        })
      }
    } finally {
      markBusy(orderId, false)
    }
  }

  if (isLoading || !categoriesReady) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-4 px-1 min-h-[400px]">
        {[1, 2, 3, 4, 5].map((placeholder) => (
          <div
            key={placeholder}
            className="w-full h-56 rounded-xl bg-[#1e1e2e] border border-white/10 animate-pulse"
          />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0 -mx-3 sm:-mx-4 min-h-screen bg-[#0d0d14]">
      <div className="sticky top-0 z-10 bg-[#0d0d14] border-b border-white/10 px-4 py-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-shrink-0">
            <ChefHat className="w-5 h-5 text-white/60" />
            <span className="text-white/70 font-bold text-sm tracking-wide uppercase">
              <T>Kuchyňa</T>
            </span>
          </div>

          <div className="flex-1 flex items-center gap-1.5 flex-wrap">
            <Tally tone="red" label={tt('Čaká')} value={newOrders.length} />
            <Tally tone="blue" label={tt('Prípr.')} value={preparingOrders.length} />
            {queuedPreorders.length > 0 && (
              <Tally
                tone="amber"
                label={tt('Čas.')}
                value={queuedPreorders.length}
                name="queued-preorders"
              />
            )}

            <div className="h-4 w-px bg-white/10 mx-0.5" />

            {counts.restaurant > 0 && (
              <ChannelTally
                icon={Store}
                short="RES"
                value={counts.restaurant}
                className="bg-red-600/25 border-red-500/30 text-red-200"
                iconClassName="text-red-300"
              />
            )}
            {counts.delivery > 0 && (
              <ChannelTally
                icon={Truck}
                short="ROZ"
                value={counts.delivery}
                className="bg-blue-600/25 border-blue-500/30 text-blue-200"
                iconClassName="text-blue-300"
              />
            )}
            {counts.bar > 0 && (
              <ChannelTally
                icon={ShoppingBag}
                short="OSB"
                value={counts.bar}
                className="bg-emerald-600/25 border-emerald-500/30 text-emerald-200"
                iconClassName="text-emerald-300"
              />
            )}

            {typeof avgCompletion?.avgMinutes === 'number' && (
              <>
                <div className="h-4 w-px bg-white/10 mx-0.5" />
                <div className="flex items-center gap-1 text-white/40 text-[11px]">
                  <Timer className="w-3.5 h-3.5" />
                  <span>
                    Ø <strong className="text-white/70">{avgCompletion.avgMinutes} min</strong>
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <CountdownRing seconds={secondsToRefresh} total={REFRESH_SECONDS} />
            <button
              type="button"
              onClick={chime.toggle}
              aria-label={chimeToggleLabel(chime.enabled)}
              data-sound-toggle={chime.enabled ? 'on' : 'off'}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/8 hover:bg-white/12 text-[11px] font-medium text-white/50 hover:text-white transition-colors"
            >
              {chime.enabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={refresh}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/8 hover:bg-white/12 text-[11px] font-medium text-white/50 hover:text-white transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> <T>Obnoviť</T>
            </button>
          </div>
        </div>
      </div>

      {/* §9.5's channel filter. Absent when there is only one channel - a strip with one tab is noise on
        * a screen a cook reads from across the room. */}
      {offered.length > 1 && (
        <div className="px-3 pt-3 space-y-1.5">
          <ChannelTabs
            channels={offered}
            selection={selection}
            counts={channelCounts}
            onToggle={(slug) => setChannelSelection(toggleChannel(slug, selection))}
            onAll={() => setChannelSelection(selectAll())}
          />
          {/* A narrowed board must not read as a quiet one. */}
          {selectionNote(selection, offered, shownOrders.length, activeOrders.length) !== '' && (
            <p className="text-[11px] text-white/50" data-channel-note>
              {selectionNote(selection, offered, shownOrders.length, activeOrders.length)}
            </p>
          )}
        </div>
      )}

      {shownOrders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-white/40">
          <CheckCircle2 className="w-16 h-16 mb-4 text-emerald-500/40" />
          <p className="text-xl font-semibold text-white/50">
            <T>Všetky objednávky sú vybavené</T>
          </p>
          <p className="text-sm mt-1 text-white/25">
            <T>Žiadne aktívne objednávky momentálne</T>
          </p>
        </div>
      )}

      {shownOrders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 py-3 px-3 overflow-y-auto">
          {shownOrders.map((order) => (
            <KitchenOrderCard
              key={order.id}
              order={order}
              lines={linesByOrder[order.id ?? 0] ?? []}
              descriptions={descriptions}
              now={now}
              preorderThresholdMinutes={preorderThresholdMinutes}
              isCompleting={completing.has(order.id ?? 0)}
              isBusy={busy.has(order.id ?? 0)}
              canAccept={hasPermission('accept_order')}
              progressOf={(key) => itemProgress[key] ?? 'idle'}
              onToggleItem={toggleItem}
              onAdvance={(target) => void advance(target)}
            />
          ))}
        </div>
      )}

      {/* §9.318, owner Q8 - legacy has this section built and gated behind `SHOW_PREORDER_QUEUE =
        * false` (`kitchen.tsx:1038`). Its wording is kept, because it is what stops a cook reading a
        * queued card as due: *"zobrazia sa N min pred doručením"*. */}
      {/* §9.404 - **the not-yet-due section is gone, and the tally in the header stays.**
        *
        * The owner: *"In Kuchyňa nástenka, do not show these predobjednávky before 60 min (for
        * example osobný odber 2 hours before was visible, but not what, etc.. its just distracting
        * there)."* The card drew the order number, the channel, the due time, the customer and a
        * **count** of the lines - *"but not what"*, exactly: it announced a job and withheld the one
        * fact a kitchen board carries.
        *
        * **Legacy built this section and switched it off** - `kitchen.tsx:36`, `const
        * SHOW_PREORDER_QUEUE = false` - so the owner has independently arrived where the system they
        * have used for years already was. §9.318 added it; what §9.318 was really filed about is a
        * pre-order that has **come due** appearing nowhere, and that half is `activeOrders` above,
        * untouched.
        *
        * `queuedPreorders` is still computed: the `Čas.` tally counts it, so a kitchen sees that
        * four are coming without four cards taking space from the food being cooked now. Every one
        * of them, with its lines, is on `PreordersPage`. */}
    </div>
  )
}

function Tally({
  tone,
  label,
  value,
  name,
}: {
  tone: 'red' | 'blue' | 'amber'
  label: string
  value: number
  /** §9.404 - a hook, so a spec can assert the count without matching an abbreviated label. */
  name?: string
}) {
  const TONES = {
    red: { box: 'bg-red-500/15 border-red-500/20', dot: 'bg-red-400' },
    blue: { box: 'bg-blue-500/15 border-blue-500/20', dot: 'bg-blue-400' },
    amber: { box: 'bg-amber-500/15 border-amber-500/20', dot: 'bg-amber-400' },
  } as const
  return (
    <div
      className={cn('flex items-center gap-1.5 border rounded px-2 py-1', TONES[tone].box)}
      data-kitchen-tally={name}
    >
      <span className={cn('w-2 h-2 rounded-full flex-shrink-0', TONES[tone].dot)} />
      <span className="text-white/50 text-[11px]">{label}</span>
      <span className="text-white font-black text-sm">{value}</span>
    </div>
  )
}

function ChannelTally({
  icon: Icon,
  short,
  value,
  className,
  iconClassName,
}: {
  icon: typeof Store
  short: string
  value: number
  className: string
  iconClassName: string
}) {
  return (
    <div className={cn('flex items-center gap-1 border rounded px-2 py-1', className)}>
      <Icon className={cn('w-3 h-3', iconClassName)} />
      <span className="text-[11px] font-bold">{short}</span>
      <span className="text-white font-black text-sm">{value}</span>
    </div>
  )
}

/** `kitchenDisplayInterval` from the franchise's general settings. */
function readThreshold(settings: unknown): number {
  const raw = (settings as { kitchenDisplayInterval?: unknown } | undefined)?.kitchenDisplayInterval
  const minutes = Number(raw)
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : DEFAULT_PREORDER_THRESHOLD_MINUTES
}

function readCurrentStatus(data: unknown): string | undefined {
  const status = (data as { currentStatus?: unknown } | null)?.currentStatus
  return typeof status === 'string' && status !== '' ? status : undefined
}

interface ProductLike {
  id?: number
  name?: string
  categoryId?: number
  kitchenDescription?: string
  showInKitchen?: boolean
}

interface CategoryLike {
  id?: number
  containsFood?: boolean
}

interface GiftRuleLike {
  id?: number
  showInKitchen?: boolean
  giftItem?: string
  giftName?: string
}

function buildVisibilityMaps(
  products: unknown,
  categories: unknown,
  amountRules: unknown,
  productRules: unknown,
): KitchenVisibilityMaps {
  const categoryOfProduct: Record<number, number | null> = {}
  for (const product of asArray<ProductLike>(products)) {
    if (product.id != null) categoryOfProduct[product.id] = product.categoryId ?? null
  }

  const categoryContainsFood: Record<number, boolean> = {}
  for (const category of asArray<CategoryLike>(categories)) {
    if (category.id != null) categoryContainsFood[category.id] = category.containsFood === true
  }

  const amountGiftRules: Record<number, boolean> = {}
  const productGiftRules: Record<number, boolean> = {}
  const giftRulesByName: Record<string, boolean> = {}

  for (const rule of asArray<GiftRuleLike>(amountRules)) {
    if (rule.id != null) amountGiftRules[rule.id] = rule.showInKitchen === true
    if (rule.giftItem?.trim()) {
      giftRulesByName[rule.giftItem.trim().toLowerCase()] = rule.showInKitchen === true
    }
  }
  for (const rule of asArray<GiftRuleLike>(productRules)) {
    if (rule.id != null) productGiftRules[rule.id] = rule.showInKitchen === true
    if (rule.giftName?.trim()) {
      giftRulesByName[rule.giftName.trim().toLowerCase()] = rule.showInKitchen === true
    }
  }

  return {
    amountGiftRules,
    productGiftRules,
    giftRulesByName,
    categoryOfProduct,
    categoryContainsFood,
  }
}

function buildDescriptions(products: unknown, recipeRows: unknown): KitchenDescriptions {
  const byId: Record<number, string> = {}
  const byName: Record<string, string> = {}
  const showInKitchen: Record<number, boolean> = {}
  const recipes: Record<number, RecipeIngredient[]> = {}
  const recipesByName: Record<string, RecipeIngredient[]> = {}

  // §9.55 — the rule lives in `recipes.ts` so it can be tested apart from the screen. Product names
  // go in with it: a recipe whose one ingredient *is* the product tells a cook nothing, and 25
  // products on this menu have exactly that.
  const nameByProductId: Record<number, string> = {}
  for (const product of asArray<ProductLike>(products)) {
    if (product.id != null && product.name) nameByProductId[product.id] = product.name
  }
  Object.assign(recipes, buildRecipeIndex(asArray<RecipeRow>(recipeRows), nameByProductId))

  for (const product of asArray<ProductLike>(products)) {
    if (product.id == null) continue
    showInKitchen[product.id] = product.showInKitchen !== false
    // A kombo half is only a name in the line, so the name is the only key available for it.
    const byThisProduct = recipes[product.id]
    if (byThisProduct && product.name) {
      recipesByName[product.name.trim().toLowerCase()] = byThisProduct
    }
    if (!product.kitchenDescription) continue
    byId[product.id] = product.kitchenDescription
    if (product.name) byName[product.name.trim().toLowerCase()] = product.kitchenDescription
  }

  return { byId, byName, showInKitchen, recipes, recipesByName }
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}
